import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import { accountingConnectionSchema, type AccountingConnection } from "@/schemas";

const startResponseSchema = z.object({
  authorize_url: z.string().url(),
  state: z.string(),
});

export const connectionKeys = {
  forCompany: (companyId: number) => ["companies", companyId, "connection"] as const,
};

export function useCompanyConnection(companyId: number | undefined) {
  return useQuery({
    queryKey: companyId ? connectionKeys.forCompany(companyId) : ["connection", "none"],
    enabled: !!companyId,
    queryFn: async (): Promise<AccountingConnection | null> => {
      const data = await api.get<unknown>(`/companies/${companyId}/connection`);
      if (!data) return null;
      return accountingConnectionSchema.parse(data);
    },
  });
}

export function useStartFreeAgentConnect() {
  return useMutation({
    mutationFn: async (args: { companyId: number; redirectUri: string }) => {
      const data = await api.post<unknown>(
        `/companies/${args.companyId}/connection/freeagent/start`,
        { redirect_uri: args.redirectUri },
      );
      return startResponseSchema.parse(data);
    },
  });
}

export function useFreeAgentCallback() {
  return useMutation({
    mutationFn: async (args: {
      code: string;
      state?: string;
      redirectUri: string;
      companyId: number;
    }) => {
      const data = await api.post<unknown>(`/auth/freeagent/callback`, {
        code: args.code,
        state: args.state ?? "",
        redirect_uri: args.redirectUri,
        company_id: args.companyId,
      });
      return data;
    },
  });
}

export function useSyncFreeAgent(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await api.post<unknown>(
        `/companies/${companyId}/connection/freeagent/sync`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectionKeys.forCompany(companyId) });
      qc.invalidateQueries({ queryKey: ["companies", companyId, "financials"] });
    },
  });
}
